import { useQuery } from '@tanstack/react-query'
import { hrefOf } from '../overview/href'
import { useEffect, useState } from 'react'
import {
  arrangeBoard,
  briefBoard,
  createBoard,
  dashboardPageQuery,
  updateBoard,
  type DashboardPage as Page,
} from '../api/dashboard'
import { moduleOf, shellQuery } from '../api/shell'
import { toast } from '../ui/notify'
import { useAdamAccess } from '../ui/adamAccess'
import { usePageStyles } from '../ui/usePageStyles'
import { BoardBuilder } from './BoardBuilder'
import { BoardTools } from './BoardTools'
import { BoardWindow } from './BoardWindow'
import { rangeOf, windowQuery, type BoardRange } from './windowRange'
import { DashboardAdviceBand } from './DashboardAdvice'
import { DashboardCardSlot } from './DashboardCard'

const SIZES: Record<string, { w: number; h: number; label: string }> = {
  '1x1': { w: 1, h: 1, label: '1 x 1' },
  '2x1': { w: 2, h: 1, label: '2 x 1' },
  '1x2': { w: 1, h: 2, label: '1 x 2' },
  '2x2': { w: 2, h: 2, label: '2 x 2' },
}

function href(path: string): string {
  return '/app/' + path.replace(/^\//, '')
}


/**
 * Tabloul central al platformei - version-3/dashboard/index.php. Classic e
 * lasat. Easeedesk nu intra in banda. Tablourile de modul raman in CI3.
 */
export function DashboardPage({ boardId }: { boardId?: number }) {
  const search = typeof window !== 'undefined' ? window.location.search : ''
  const page = useQuery(dashboardPageQuery(boardId, search))
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(['css/od_dashboard.css'], shell.data?.version ?? '')

  const redirect = page.data?.redirect ?? ''
  useEffect(() => {
    if (redirect) window.location.assign(redirect)
  }, [redirect])

  if (page.isPending) return <div className="odb-page" />
  if (page.isError || !page.data || redirect) return <div className="odb-page" />

  return <DashboardChrome page={page.data} search={search} />
}

function DashboardChrome({ page, search }: { page: Page; search: string }) {
  /* "Ask the agent" duce la sertarul lui Adam: fara acces la el, nici butonul. */
  const adamAccess = useAdamAccess()
  const params = new URLSearchParams(search)
  const days = page.scope.days
  const klass = page.scope.class
  /* Perioada asezata de server - null cand tabloul merge pe una din cele patru
     ferestre fixe. Tot ce leaga bara de sus o poarta mai departe, altfel un
     clic pe o clasa de active ar pierde perioada fara sa spuna nimeni. */
  const range = rangeOf(page.scope)
  /** Adresa tabloului, cu fereastra si clasa pe ea. */
  const boardHref = (n: number, chosen: BoardRange | null, cls: string = klass) =>
    href(`${page.boardUrl}${windowQuery(n, cls, chosen)}`)
  const [busy, setBusy] = useState(false)
  const [dialog, setDialog] = useState<{
    title: string
    text: string
    input?: boolean
    ok: string
    danger?: boolean
    then: (value: string) => void
  } | null>(null)
  const [name, setName] = useState('')

  const basedOn = page.board.kind === 'view' ? page.board.key : 'technician'
  const wallKind = page.board.kind
  const wallKey = page.board.key

  async function onArrange() {
    setBusy(true)
    try {
      const out = await arrangeBoard(page.board.kind, page.board.key, page.board.key)
      window.location.assign(href(`dashboard/board/${out.id}?edit=1`))
    } catch (err) {
      setBusy(false)
      toast(err instanceof Error ? err.message : 'That did not work.', 'Error', 'error')
    }
  }

  function onReset() {
    const id = page.board.override
    setDialog({
      title: 'Back to the default arrangement?',
      text: 'Your arrangement of this board goes away; the cards come back as we composed them.',
      ok: 'Reset',
      danger: true,
      then: () => {
        void updateBoard(id, 'delete').then(() => window.location.reload())
      },
    })
  }

  function onNew() {
    setName('')
    setDialog({
      title: 'New board',
      text: 'It starts with the cards of the board you are on, so you have something to edit.',
      input: true,
      ok: 'Create',
      then: (value) => {
        void createBoard(value, basedOn).then((out) => {
          window.location.assign(href(`dashboard/board/${out.id}?edit=1`))
        })
      },
    })
  }

  async function onAsk() {
    try {
      const out = await briefBoard(page.board.kind, page.board.key, days)
      const ask = (window as unknown as { odAiAsk?: (q: string, opt: { fresh: boolean }) => void }).odAiAsk
      if (typeof ask !== 'function') {
        toast('The agent panel is not on this page.', 'Error', 'error')
        return
      }
      if (!out.question) {
        toast('There is nothing on this board to look at.', 'Error', 'error')
        return
      }
      ask(out.question, { fresh: true })
    } catch (err) {
      toast(err instanceof Error ? err.message : 'That did not work.', 'Error', 'error')
    }
  }

  const empty = page.cards.length === 0 && !page.board.edit

  return (
    <div
      className="odb-page"
      data-based-on={basedOn}
      data-save-url="/api/v2/dashboard/boards"
      data-update-url="/api/v2/dashboard/boards"
      data-arrange-url="/api/v2/dashboard/arrange"
      data-create-url="/api/v2/dashboard/boards"
    >
      <nav className="odb-modules">
        {Object.entries(page.modules).map(([key, meta]) => (
          <a
            className={`odb-module odb-m-${key}`}
            href={hrefOf(meta.href) ?? meta.href}
            key={key}
          >
            <i className={`fas ${meta.icon}`} aria-hidden="true"></i> {meta.label}
          </a>
        ))}
      </nav>
      <div className="odb-bar-top">
        <nav className="odb-views">
          {Object.entries(page.views).map(([key, meta]) => (
            <a
              className={`odb-view${page.board.kind === 'view' && key === page.board.key ? ' is-on' : ''}`}
              title={meta.about}
              href={href(`dashboard?view=${key}&${windowQuery(days, klass, range)}`)}
              key={key}
            >
              <i className={`fas ${meta.icon}`} aria-hidden="true"></i> {meta.label}
            </a>
          ))}
        </nav>
        <nav className="odb-classes" aria-label="Asset class">
          {Object.entries(page.classes).map(([key, meta]) => (
            <a
              className={`odb-class${key === klass ? ' is-on' : ''}`}
              title={meta.about}
              href={boardHref(days, range, key)}
              key={key}
            >
              <i className={`fas ${meta.icon}`} aria-hidden="true"></i> {meta.label}
            </a>
          ))}
        </nav>
        <BoardWindow
          windows={page.windows}
          days={days}
          range={range}
          said={page.scope.rangeSaid}
          maxDays={page.scope.maxDays}
          hrefFor={(n, chosen) => boardHref(n, chosen)}
        />
        <a
          className="odb-wallbtn"
          target="_blank"
          rel="noopener"
          title="Open this board on a TV: no menu, big type, rotates by itself"
          href={href(`dashboard/wall/${wallKind}/${wallKey}${params.toString() ? `?${params}` : ''}`)}
        >
          <i className="fas fa-tv" aria-hidden="true"></i> Wallboard
        </a>
        <a
          className="odb-wallbtn"
          title="The numbers on this board, as a spreadsheet"
          href={`/api/v2/dashboard/export/${wallKind}/${wallKey}?${windowQuery(days, klass, range)}`}
        >
          <i className="fas fa-file-csv" aria-hidden="true"></i> CSV
        </a>
        <div className="odb-bar-right">
          {page.board.kind !== 'board' && (
            <>
              <button
                type="button"
                className="odb-btn odb-btn-main"
                id="odbArrange"
                data-kind={page.board.kind}
                data-key={page.board.key}
                data-view={page.board.key}
                title="Move and resize the cards of this board; it stays yours"
                disabled={busy}
                onClick={() => void onArrange()}
              >
                <i className="fas fa-sliders-h" aria-hidden="true"></i> Arrange
              </button>
              {page.board.override > 0 && (
                <button
                  type="button"
                  className="odb-btn"
                  id="odbResetOverride"
                  data-board={page.board.override}
                  title="Back to the default arrangement of this board"
                  onClick={onReset}
                >
                  <i className="fas fa-undo" aria-hidden="true"></i> Reset to default
                </button>
              )}
            </>
          )}
          {adamAccess && page.aiOn && page.cards.length > 0 && !page.board.edit && (
            <button
              type="button"
              className="odb-btn odb-advice-ask"
              id="odbAsk"
              title="Sends every number on this board to Adam, in a conversation of its own"
              onClick={() => void onAsk()}
            >
              <i className="fas fa-comment-dots" aria-hidden="true"></i> Ask the agent
            </button>
          )}
          {page.boards.map((mine) => (
            <a
              className={`odb-board${page.board.kind === 'board' && String(mine.id) === page.board.key ? ' is-on' : ''}`}
              href={href(`dashboard/board/${mine.id}?${windowQuery(days, klass, range)}`)}
              title={mine.mine ? 'Yours' : `Shared by ${mine.owner}`}
              key={mine.id}
            >
              {mine.default && <i className="fas fa-thumbtack" aria-hidden="true"></i>} {mine.name}
              {!mine.mine && <i className="fas fa-users odb-board-shared" aria-hidden="true"></i>}
            </a>
          ))}
          <button type="button" className="odb-board odb-board-new" id="odbNewBoard" title="A board of your own: arrange it, add cards, make your own" onClick={onNew}>
            <i className="fas fa-plus" aria-hidden="true"></i> New board
          </button>
        </div>
        {(page.noCompany || !page.history) && (
          <p className="odb-lead">
            {!page.history && (
              <span className="odb-lead-warn">
                The day-by-day lines under the numbers are not being recorded yet - the dashboard tables are missing. Apply
                the schema from Change Log; the first line appears the day after, when there are two days to draw.
              </span>
            )}
            {/* Fara randul "N of M cards could not be read - that is not the same as zero" (18 septembrie
                2026): omul il citea ca pe o eroare. Un card fara date isi spune singur, linistit, "No data
                yet" - vezi `CardMarkup.php`. `tally` ramane in raspuns, neschimbat. */}
            {page.noCompany && (
              <span className="odb-lead-warn">
                No company is selected, so the cards that count within one company have nothing to read. Pick one from the
                top bar.
              </span>
            )}
          </p>
        )}
      </div>
      {/* Panoul masinii si actiunile de pe card - `peek.js` / `act.js`. */}
      <BoardTools days={days} klass={klass} />
      {page.board.kind === 'board' && page.board.board?.mine && !page.board.edit && (
        <div className="odb-editbar" id="odbEditBar" data-board={page.board.board.id} data-editing="0">
          <a className="odb-btn odb-btn-main" href={href(`dashboard/board/${page.board.board.id}?edit=1`)}>
            <i className="fas fa-sliders-h" aria-hidden="true"></i> Edit this board
          </a>
          <button type="button" className="odb-btn" id="odbRename" onClick={() => rename(page.board.board!.id, setDialog)}>
            Rename
          </button>
          <button
            type="button"
            className="odb-btn"
            id="odbDefault"
            disabled={page.board.board.default}
            onClick={() => void updateBoard(page.board.board!.id, 'default').then(() => window.location.reload())}
          >
            {page.board.board.default ? 'Opens by default' : 'Open this by default'}
          </button>
          <button
            type="button"
            className="odb-btn"
            id="odbShare"
            data-on={page.board.board.shared ? '1' : '0'}
            onClick={() =>
              void updateBoard(page.board.board!.id, 'share', { on: page.board.board!.shared ? 0 : 1 }).then(() =>
                window.location.reload(),
              )
            }
          >
            {page.board.board.shared ? 'Stop sharing' : 'Share with the company'}
          </button>
          <button
            type="button"
            className="odb-btn odb-btn-danger"
            id="odbDelete"
            onClick={() =>
              setDialog({
                title: 'Delete this board?',
                text: 'The board and its arrangement go away. The cards themselves stay in the catalog.',
                ok: 'Delete',
                danger: true,
                then: () => {
                  void updateBoard(page.board.board!.id, 'delete').then(() => window.location.assign(href('dashboard')))
                },
              })
            }
          >
            Delete
          </button>
        </div>
      )}
      <div className="odb-dialog" id="odbDialog" hidden={!dialog}>
        <div className="odb-dialog-box" role="dialog" aria-modal="true" aria-labelledby="odbDialogTitle">
          <h4 className="odb-dialog-title" id="odbDialogTitle">
            {dialog?.title}
          </h4>
          <p className="odb-dialog-text" id="odbDialogText">
            {dialog?.text}
          </p>
          <input
            type="text"
            className="form-control odb-dialog-input"
            id="odbDialogInput"
            hidden={!dialog?.input}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <div className="odb-dialog-foot">
            <button type="button" className="odb-btn" id="odbDialogNo" onClick={() => setDialog(null)}>
              Cancel
            </button>
            <button
              type="button"
              className={`odb-btn odb-btn-main${dialog?.danger ? ' odb-btn-danger' : ''}`}
              id="odbDialogYes"
              onClick={() => {
                const next = dialog
                setDialog(null)
                next?.then(name)
              }}
            >
              {dialog?.ok ?? 'OK'}
            </button>
          </div>
        </div>
      </div>
      {page.board.edit && page.board.board?.mine ? (
        /* Constructorul: bara lui de editare, grila care se muta, catalogul. */
        <BoardBuilder boardId={page.board.board.id} cards={page.cards} placed={page.board.placed} />
      ) : empty ? (
        <div className="odb-empty">
          <i className="fas fa-lock" aria-hidden="true"></i> Nothing here is open to you. Ask an administrator for the
          rights, or pick another perspective above.
        </div>
      ) : (
        <>
          {/* Sfatul e o banda DEASUPRA grilei, nu un loc in ea - vezi `DashboardAdvice.tsx`. */}
          {page.cards.length > 0 && <DashboardAdviceBand advice={page.advice} />}
          <div className="odb" id="odbGrid">
            {page.cards.map((card) => (
              <DashboardCardSlot card={card} edit={false} sizes={SIZES} key={card.id} />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function rename(id: number, setDialog: (d: { title: string; text: string; input: boolean; ok: string; then: (v: string) => void } | null) => void) {
  setDialog({
    title: 'Rename this board',
    text: 'The name is what you and the people you share it with see in the list.',
    input: true,
    ok: 'Rename',
    then: (value) => {
      void updateBoard(id, 'rename', { name: value }).then(() => window.location.reload())
    },
  })
}
